import React from 'react';
import { Box, Typography } from '@mui/material';
import { useAuth } from '../../AuthContext';
import { useQuery } from '@tanstack/react-query';
import { fetchFacultySchedule } from '../../services/resourceService';
import './ClassScheduleTab.css';

// Accent colors for schedule blocks
const accentColors = [
  { bg: "#eaf2fc", border: "#3984ec", time: "#3984ec" },
  { bg: "#e4fff5", border: "#34bc7c", time: "#34bc7c" },

];

function getAccent(i) {
  return accentColors[i % accentColors.length];
}

function timeRange(startTime, durationMins) {
  const [h, m] = startTime.split(":").map(Number);
  const start = new Date(2000, 1, 1, h, m);
  const end = new Date(start.getTime() + durationMins * 60000);
  const fmt = (d) =>
    d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false })
      .replace(/^24:/, '00:');
  return fmt(start) + "–" + fmt(end);
}

const dayOrder = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const dayAbbreviations = ["M", "T", "W", "T", "F", "S", "S"];

const ClassScheduleTab = ({ selectedUser, facultyInfo }) => {
  const { user: loggedInUser } = useAuth();
  
  // Standardize userId extraction: prioritize user.userId, then rawData, then facultyInfo
  const userId = selectedUser?.userId || 
                 selectedUser?.rawData?.user?.userId || 
                 facultyInfo?.userId || 
                 loggedInUser?.userId || 
                 'unknown';

  const [selectedDay, setSelectedDay] = React.useState(() => {
    return new Date().toLocaleString('en-US', { weekday: 'long' });
  });

  const { data: classesData = [], isLoading, error } = useQuery({
    queryKey: ['facultySchedule', userId],
    queryFn: () => fetchFacultySchedule(userId),
    enabled: !!userId && userId !== 'unknown',
    staleTime: 10 * 60 * 1000, // 10 minutes cache
    refetchOnWindowFocus: false,
  });

  // Merge by day
  const mergedScheduleByDay = {};
  classesData.forEach(cls => {
    Object.entries(cls.facultySchedule || {}).forEach(([day, sessions]) => {
      if (!mergedScheduleByDay[day]) mergedScheduleByDay[day] = [];
      sessions.forEach(session => {
        mergedScheduleByDay[day].push({
          ...session,
          className: cls.className,
          sectionName: cls.sectionName,
        });
      });
    });
  });

  Object.values(mergedScheduleByDay).forEach(sessions =>
    sessions.sort((a, b) => a.startTime.localeCompare(b.startTime))
  );

  if (isLoading) return <Typography className="schedule-loading-fdb">Loading schedule...</Typography>;
  if (error) return <Typography className="schedule-error-fdb">Error loading schedule</Typography>;

  return (
    <Box className="tsc-card">
      <Typography variant="h6" className="schedule-title-fdb">
        Today's Schedule
      </Typography>
      <Box className="day-selector-fdb">
        {dayOrder.map((day, index) => (
          <Box
            key={day}
            className={`day-button-fdb ${selectedDay === day ? 'day-button-selected-fdb' : ''}`}
            onClick={() => setSelectedDay(day)}
          >
            {dayAbbreviations[index]}
          </Box>
        ))}
      </Box>

      {!mergedScheduleByDay[selectedDay] || mergedScheduleByDay[selectedDay].length === 0 ? (
        <Typography className="no-schedule-fdb">
          No scheduled classes found for {selectedDay}.
        </Typography>
      ) : (
        <Box className="schedule-list-fdb">
          {mergedScheduleByDay[selectedDay].map((s, idx) => {
            const accent = getAccent(idx);
            return (
              <Box
                key={idx}
                className="schedule-card-fdb"
                style={{
                  background: accent.bg,
                  borderLeft: `8px solid ${accent.border}`,
                }}
              >
                <Box className="schedule-info-fdb">
                  <Typography className="course-name-fdb" style={{ color: accent.border }}>
                    {s.courseName}
                  </Typography>
                  <Typography className="course-details-fdb" style={{ color: accent.border }}>
                    {s.courseCode} — {s.location}
                  </Typography>
                  {s.topic && (
                    <Typography className="topic-text-fdb">
                      Topic: {s.topic}
                    </Typography>
                  )}
                  <Typography className="class-section-fdb">
                    Class: {s.className} {s.sectionName}
                  </Typography>
                </Box>
                <Box
                  className="time-box-fdb"
                  style={{ backgroundColor: accent.time }}
                >
                  {timeRange(s.startTime, parseInt(s.durationMins, 10))}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default ClassScheduleTab;